/* eslint-disable no-param-reassign */
/* eslint-disable consistent-return */
/* eslint-disable no-console */
/*

Helper functions to interface with the /auth api. Token saved in localStorage.

*/

import axios from 'axios'
import queryString from 'query-string'
import * as Auth from 'aws-amplify/auth'
import * as FullStory from '@fullstory/browser'
import { configureAmplify, getAmplifyClientMetadata } from '../../../../services/actions'
import { sendDatadogError } from '../../../services/datadog'
import { getDarklyFlags } from '../../Session/services/selectors/darklySelectors'

// ACTIONS
export const LOGOUT = 'LOGOUT'
export const SET_TOKEN = 'SET_TOKEN'
export const WHO_AM_I = 'WHO_AM_I'
export const AUTH_ERROR = 'AUTH_ERROR'
export const AUTH_SUCCESS = 'AUTH_SUCCESS'
export const SET_AUTH_CONFIG = 'SET_AUTH_CONFIG'
export const AUTH_TYPEFORM_SURVEY = 'AUTH_TYPEFORM_SURVEY'
export const VALIDATE_INVITE_LINK = 'VALIDATE_INVITE_LINK'
export const VALIDATE_INVITE_LINK_ERROR = 'VALIDATE_INVITE_LINK_ERROR'
export const TYPE_FORM_HEALTHY = 'TYPE_FORM_HEALTHY'
export const RE_SEND_INVITE_DONE = 'RE_SEND_INVITE_DONE'

export function login(username, password) {
  return async (dispatch, getState) => {
    const darklyFlags = getDarklyFlags(getState())
    const { initFullStory } = darklyFlags

    const DEFAULT_ERROR_MESSAGE = {
      type: 'AUTH_ERROR',
      error: ['Incorrect username and/or password.']
    }

    if (!username || !password) {
      dispatch(DEFAULT_ERROR_MESSAGE)
    }

    try {
      await Auth.signIn({
        username: username.toLowerCase(),
        password
      })
      const session = await Auth.fetchAuthSession()
      // since the migrate-user Cognito lambda trigger always sets the finalUserStatus=CONFIRMED
      // we never have user.challengeName === 'NEW_PASSWORD_REQUIRED' which would require the front-end
      // to ask the user to set up a new password. So in our case,
      // The user directly signs in
      const token = `Bearer ${session.tokens.idToken}`
      dispatch(setToken({ token, isFederatedLogin: false }))

      window.location.reload()

      if (initFullStory) {
        FullStory.event('FS_USER_LOGGED_IN', {
          username
        })
      }
    } catch (err) {
      sendDatadogError(`Failed login for username ${username}`, 'error', err)

      if (
        ['AuthError', 'NotAuthorizedException', 'InvalidParameterException'].indexOf(err.name) < 0
      ) {
        dispatch({
          ...DEFAULT_ERROR_MESSAGE,
          error: [
            'An unexpected error occurred while processing your information, ' +
              'please wait a few minutes, refresh the page and try again, if the ' +
              'problem persists contact our support team.'
          ]
        })
      } else {
        dispatch(DEFAULT_ERROR_MESSAGE)
      }
    }
  }
}

export function fetchAuthConfig() {
  return async (dispatch) => {
    try {
      const authConfig = (await axios.get('/auth/config/')).data
      if (authConfig && authConfig.user_pool_client_id) {
        configureAmplify(authConfig)
        dispatch({
          type: SET_AUTH_CONFIG,
          data: authConfig
        })
        return authConfig
      }
    } catch (error) {
      sendDatadogError('Could not retrieve auth config', 'error', error)
    }
    dispatch({
      type: SET_AUTH_CONFIG,
      data: false
    })
    return false
  }
}

export function sendResetEmail(email) {
  return async (dispatch) => {
    if (!email) {
      return dispatch({
        type: 'AUTH_ERROR',
        error: ['No email entered.']
      })
    }

    try {
      await Auth.resetPassword({
        username: email.toLowerCase(),
        options: { clientMetadata: getAmplifyClientMetadata() }
      })
      return dispatch(
        authSuccess(
          'If this user exists, you will receive instructions on how to reset your password.'
        )
      )
    } catch (e) {
      let message = 'Could not reset the password. If the problem persist, please contact Support.'
      if (e.message) {
        message = `${e.message} If the problem persist, please contact Support.`
      }

      sendDatadogError(message, 'error', e)

      return dispatch({
        type: 'AUTH_ERROR',
        error: [message]
      })
    }
  }
}

export function reSendAdminInvite(email) {
  return async (dispatch) => {
    try {
      await axios.get(`/employee/re_send_admin_invite?email=${email}`)
      return dispatch({
        type: 'RE_SEND_INVITE_DONE',
        done: true
      })
    } catch (error) {
      return dispatch({
        type: 'RE_SEND_INVITE_DONE',
        done: false
      })
    }
  }
}
export function confirmPasswordReset({
  email,
  user,
  password,
  passwordConfirm,
  token,
  isInitialPassword = false
}) {
  const linkError =
    'Your forgot password link is invalid or expired. Please try resetting your password again.'
  return async (dispatch) => {
    if (!password || !passwordConfirm || password !== passwordConfirm)
      dispatch(
        authError({
          response: {
            data: { Error: 'Passwords do not match.' }
          }
        })
      )
    else if (!token)
      dispatch(
        authError({
          response: {
            data: { Error: linkError }
          }
        })
      )
    else
      try {
        if (!isInitialPassword) {
          await Auth.confirmResetPassword({
            username: email.toLowerCase(),
            confirmationCode: token,
            newPassword: password
          })
        } else {
          await axios.post('/user/password/reset/confirm/', {
            user,
            password,
            token
          })
        }
        dispatch(login(email, password))
      } catch (err) {
        let error = err
        if (
          error.response &&
          error.response.data &&
          error.response.data.Error === 'Invalid token.'
        ) {
          error.response.data.Error = linkError
        }
        if (!error.response) {
          error = { response: { data: { Error: [error.message] } } }
        }
        dispatch(authError(error))
      }
  }
}

export function authClearMessages() {
  return (dispatch) => {
    dispatch({
      type: AUTH_ERROR,
      error: {}
    })

    dispatch({
      type: AUTH_SUCCESS,
      success: []
    })
  }
}

export function authError(res) {
  const response = (res && res.response) || res
  return (dispatch) => {
    if (typeof response === 'string') {
      dispatch({
        type: AUTH_ERROR,
        error: [response]
      })
    } else if (response.status === 500) {
      dispatch({
        type: AUTH_ERROR,
        error: {
          'Unknown Error': ['Sorry, something went wrong.']
        }
      })
    } else {
      const data = (response && response.data) || ['Unknown error.']
      dispatch({
        type: 'AUTH_ERROR',
        error: data
      })
    }
  }
}

export function authSuccess(message) {
  return (dispatch) => {
    dispatch({
      type: AUTH_SUCCESS,
      success: [message]
    })
  }
}

export function setToken(JWT) {
  return (dispatch) => {
    if (localStorage) {
      if (JWT.token) {
        localStorage.setItem('JWT', JWT.token)
        if (JWT.isFederatedLogin !== undefined) {
          localStorage.setItem('isFederatedLogin', JWT.isFederatedLogin)
        }
      } else {
        localStorage.removeItem('JWT')
        localStorage.removeItem('isFederatedLogin')
      }
    }
    dispatch({
      type: SET_TOKEN,
      data: JWT
    })
  }
}

/**
 * The validateInviteLink is responsible for validate
 * the invite link when the admin is setting the password
 * for the first time, the link provides a email and token
 *
 * @export { function}
 * @param {*} { email, token }
 * @returns {void}
 */
export function validateInviteLink(queryParams) {
  const { email, token } = queryString.parse(queryParams)
  return async (dispatch) => {
    if (!email || !token) {
      throw new Error(`Params for the invite link can't be empty`)
    }
    try {
      const { status } = await axios.get(`/user/password/reset/check_token${queryParams}`)

      dispatch({
        type: VALIDATE_INVITE_LINK,
        data: {
          isValidationTokenDone: true,
          isValidToken: status === 200,
          tokenError: null
        }
      })
    } catch (error) {
      dispatch({
        type: VALIDATE_INVITE_LINK_ERROR,
        data: {
          isValidationTokenDone: true,
          isValidToken: false,
          tokenError: error.message
        }
      })
    }
  }
}
